import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import ContractTrading from './ContractTrading';
import Login from './Login';
import Arbitrage from './Arbitrage';
import Options from './Options';
import TradeHistory from './TradeHistory';
import TradeJournal from './TradeJournal';
import UserManagement from './UserManagement';
import AITradingPanel from './AITradingPanel';
import TradFiTrading from './TradFiTrading';
import CredentialManager from './CredentialManager';
import { PriceProvider, usePrices } from '../contexts/PriceContext';
import environmentManager from '../config/environment';

const DashboardContent = () => {
    const { prices, isConnected, connectionStatus } = usePrices();
    const [currentPage, setCurrentPage] = useState('overview');
    const [botStatus, setBotStatus] = useState({
        isRunning: false,
    simulationMode: true
  });
  const [isSwitchingEnvironment, setIsSwitchingEnvironment] = useState(false);
  const [backendStatus, setBackendStatus] = useState({
    connected: false,
    lastAnalysis: null,
    analysisCount: 0
  });
  const [bybitConnected, setBybitConnected] = useState(false);
  const [bybitBalance, setBybitBalance] = useState(0);
  const [bybitConnectionStatus, setBybitConnectionStatus] = useState('Not connected');
  
  // Fetch Bybit connection status and balance
  useEffect(() => {
    const fetchBybitData = async () => {
      try {
        // Check connection status
        const statusResponse = await fetch('/api/bybit/status');
        const statusData = await statusResponse.json();
        
        if (statusData.connected) {
          setBybitConnected(true);
          setBybitConnectionStatus(`Connected to Bybit ${statusData.accountType || 'Testnet'}`);
          
          // Fetch balance
          const balanceResponse = await fetch('/api/bybit/balance');
          const balanceData = await balanceResponse.json();
          
          console.log('📥 Dashboard Balance API response:', balanceData);
          
          if (balanceData.success && balanceData.balance) {
            const availableBalance = balanceData.balance.available || balanceData.balance.availableToWithdraw || balanceData.balance.equity || 0;
            setBybitBalance(availableBalance);
            console.log('✅ Dashboard Bybit balance updated:', availableBalance);
            console.log('📊 Full balance object:', balanceData.balance);
          } else {
            console.error('❌ Dashboard balance fetch failed:', balanceData.error || 'Unknown error');
            console.error('❌ Full balance response:', balanceData);
            setBybitBalance(0);
          }
        } else {
          setBybitConnected(false);
          setBybitConnectionStatus(statusData.message || 'Not connected to Bybit');
          setBybitBalance(0);
        }
      } catch (error) {
        console.error('❌ Error fetching Bybit data:', error);
        setBybitConnected(false);
        setBybitConnectionStatus('Connection error');
        setBybitBalance(0);
      }
    };
    
    // Fetch immediately
    fetchBybitData();
    
    // Update every 5 seconds
    const interval = setInterval(fetchBybitData, 5000);
    
    return () => clearInterval(interval);
  }, []);
  
  const [allTrades, setAllTrades] = useState([]);

  // Load trade history from server on mount
  useEffect(() => {
    fetch('/api/trades')
      .then(r => r.json())
      .then(data => {
        if (data.success && Array.isArray(data.trades)) {
          setAllTrades(data.trades);
          console.log(`📋 Loaded ${data.trades.length} trades from server`);
        }
      })
      .catch(e => console.warn('⚠️  Could not load trade history:', e.message));
  }, []);

  // Calculate accurate statistics - Use Bybit data if available, prevent Infinity
  const calculateTradePnL = (trade) => {
    if (trade.status === 'CLOSED') {
      return trade.profitLoss || 0;
    }
    
    // CRITICAL: If we have Bybit position data, use it directly (source of truth)
    if (trade.bybitPosition) {
      const bybitPnl = parseFloat(trade.bybitPosition.unrealisedPnl || trade.bybitPosition.unrealizedPnL || trade.profitLoss || 0);
      return isFinite(bybitPnl) ? bybitPnl : 0;
    }
    
    // If we have profitLoss already set (from Bybit sync), use it
    if (trade.profitLoss !== undefined && trade.profitLoss !== null) {
      const pnl = parseFloat(trade.profitLoss);
      return isFinite(pnl) ? pnl : 0;
    }
    
    // Fallback: Calculate from prices (only if entryPrice is valid)
    const entryPrice = parseFloat(trade.entryPrice || 0);
    const currentPrice = parseFloat(trade.currentPrice || 0);
    
    // Prevent division by zero
    if (!entryPrice || entryPrice === 0 || !isFinite(entryPrice)) {
      console.warn(`⚠️  Cannot calculate P&L for ${trade.pair}: entryPrice is ${entryPrice}`);
      return 0;
    }
    
    if (!currentPrice || currentPrice === 0 || !isFinite(currentPrice)) {
      console.warn(`⚠️  Cannot calculate P&L for ${trade.pair}: currentPrice is ${currentPrice}`);
      return 0;
    }
    
    const priceChange = trade.action === 'BUY' 
      ? (currentPrice - entryPrice) / entryPrice
      : (entryPrice - currentPrice) / entryPrice;
    
    const pnl = priceChange * (trade.leverage || 1) * (trade.amount || 0);
    return isFinite(pnl) ? pnl : 0;
  };

  const totalProfit = allTrades.reduce((sum, trade) => sum + calculateTradePnL(trade), 0);
  
  const dailyProfit = allTrades
    .filter(trade => {
      const tradeDate = new Date(trade.timestamp);
      const today = new Date();
      return tradeDate.toDateString() === today.toDateString();
    })
    .reduce((sum, trade) => sum + calculateTradePnL(trade), 0);

  const totalTrades = allTrades.length;
  const activeTrades = allTrades.filter(trade => trade.status === 'ACTIVE').length;
  const closedTrades = allTrades.filter(trade => trade.status === 'CLOSED').length;
  const winningTrades = allTrades.filter(trade => trade.status === 'CLOSED' && (trade.profitLoss || 0) > 0).length;
  const winRate = closedTrades > 0 ? (winningTrades / closedTrades * 100) : 0;
  const avgProfitPerTrade = closedTrades > 0 ? (totalProfit / closedTrades) : 0;

  // Keep localStorage as a session cache too (fast UI restore on re-render)
  useEffect(() => {
    if (allTrades.length > 0) localStorage.setItem('all_trades_history', JSON.stringify(allTrades));
  }, [allTrades]);

  // Check backend status on component mount and periodically
  useEffect(() => {
    checkBackendStatus();
    const interval = setInterval(checkBackendStatus, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, []);

  // Add trade function — preserves ID from Bybit order so server record matches
  const addTrade = (trade) => {
    const newTrade = {
      ...trade,
      id: trade.id || Date.now().toString(),
      timestamp: trade.timestamp || new Date().toISOString()
    };
    setAllTrades(prev => {
      // Avoid duplicates (e.g. if position sync re-adds)
      if (prev.find(t => t.id === newTrade.id)) return prev;
      return [newTrade, ...prev];
    });
    console.log('Trade added:', newTrade);
  };

  // Update trade function - FORCE update even if values seem the same
  const updateTrade = (tradeId, updates) => {
    setAllTrades(prev => {
      const updated = prev.map(trade => {
        if (trade.id === tradeId) {
          const newTrade = { 
            ...trade, 
            ...updates,
            _updateTimestamp: Date.now() // Force React to detect change
          };
          console.log(`🔄 Updating trade ${tradeId}:`, {
            old: { 
              entryPrice: trade.entryPrice, 
              currentPrice: trade.currentPrice, 
              profitLoss: trade.profitLoss,
              bybitPosition: trade.bybitPosition ? 'present' : 'missing'
            },
            new: { 
              entryPrice: newTrade.entryPrice, 
              currentPrice: newTrade.currentPrice, 
              profitLoss: newTrade.profitLoss,
              bybitPosition: newTrade.bybitPosition ? 'present' : 'missing'
            }
          });
          return newTrade;
        }
        return trade;
      });
      console.log(`✅ Trade ${tradeId} updated in state. Total trades: ${updated.length}`);
      return updated;
    });
  };

  // Close trade function
  const closeTrade = (tradeId, updates = {}) => {
    setAllTrades(prev => prev.map(trade =>
      trade.id === tradeId
        ? { ...trade, status: 'CLOSED', closedAt: new Date().toISOString(), ...updates }
        : trade
    ));
    console.log('Trade closed:', tradeId);
  };

  // Handle environment switching with loading popup
  const handleEnvironmentSwitch = (environment) => {
    setIsSwitchingEnvironment(true);
    
    // Show loading popup briefly, then switch immediately
    setTimeout(() => {
      environmentManager.switchEnvironment(environment);
      setIsSwitchingEnvironment(false);
      
      // Show success message
      alert(`Environment switched to ${environment} mode successfully!`);
    }, 1000); // Increased to 1 second for better UX
  };

  // Check backend connection status
  const checkBackendStatus = async () => {
    try {
      const response = await fetch('/api/status', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBackendStatus(prev => ({
          connected: true,
          lastAnalysis: new Date().toLocaleTimeString(),
          analysisCount: prev.analysisCount + 1
        }));
      } else {
        setBackendStatus(prev => ({ ...prev, connected: false }));
      }
    } catch (error) {
      setBackendStatus(prev => ({ ...prev, connected: false }));
    }
  };

  // Toggle bot
  const toggleBot = async () => {
    try {
      const endpoint = botStatus.isRunning ? '/api/bot/stop' : '/api/bot/start';
      const response = await fetch(`${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || `HTTP ${response.status}: ${response.statusText}` };
        }
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Check for success flag or started/stopped flags
      if (result.success || result.started || result.stopped) {
        const wasRunning = botStatus.isRunning;
        setBotStatus(prev => ({ ...prev, isRunning: !prev.isRunning }));
        
        if (!wasRunning) {
          alert(`✅ Bot started in ${botStatus.simulationMode ? 'SIMULATION' : 'REAL'} mode`);
        } else {
          alert('✅ Bot stopped successfully');
        }
      } else {
        alert(`❌ Error: ${result.error || 'Failed to control bot'}`);
      }
    } catch (error) {
      console.error('❌ Error toggling bot:', error);
      alert(`❌ Failed to toggle bot: ${error.message || 'Check backend connection'}`);
    }
  };

  // Render page content based on current page
    const renderPage = () => {
        switch (currentPage) {
            case 'overview':
                return (
          <div className="overview-page">

            {/* Bybit Connection Status Banner */}
            <div className={`bybit-status-banner ${bybitConnected ? 'connected' : 'disconnected'}`} style={{
              padding: '15px 20px',
              marginBottom: '20px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: bybitConnected ? '#d4edda' : '#f8d7da',
              border: `2px solid ${bybitConnected ? '#28a745' : '#dc3545'}`,
              color: bybitConnected ? '#155724' : '#721c24'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <span style={{ fontSize: '24px' }}>{bybitConnected ? '🟢' : '🔴'}</span>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                    Bybit Status: {bybitConnected ? 'Connected' : 'Not Connected'}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.8 }}>
                    {bybitConnectionStatus}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', opacity: 0.7 }}>Available Balance</div>
                  <div style={{ fontWeight: 'bold', fontSize: '20px' }}>
                    {bybitConnected ? `$${bybitBalance.toFixed(2)}` : '$0.00'}
                  </div>
                </div>
              </div>
            </div>

            {/* Mode Indicator */}
            <div className="mode-indicator-banner">
              <div className="mode-info">
                <div className="mode-header">
                  <span className="mode-icon">
                    {environmentManager.currentEnvironment === 'DEVELOPMENT' ? '🔧' : '🚀'}
                  </span>
                  <div className="mode-text-section">
                    <span className="mode-text">
                      {environmentManager.currentEnvironment === 'DEVELOPMENT' ? 'Development Mode' : 'Production Mode'}
                    </span>
                    <span className="mode-description">
                      {environmentManager.currentEnvironment === 'DEVELOPMENT' 
                        ? 'Safe testing environment with full debugging' 
                        : 'Live trading environment with real money'}
                    </span>
                  </div>
                </div>
                <div className="mode-switcher">
                  <button 
                    className={`mode-switch-btn ${environmentManager.currentEnvironment === 'DEVELOPMENT' ? 'active' : ''}`}
                    onClick={() => handleEnvironmentSwitch('DEVELOPMENT')}
                    disabled={isSwitchingEnvironment}
                  >
                    Development
                  </button>
                  <button 
                    className={`mode-switch-btn ${environmentManager.currentEnvironment === 'PRODUCTION' ? 'active' : ''}`}
                    onClick={() => handleEnvironmentSwitch('PRODUCTION')}
                    disabled={isSwitchingEnvironment}
                  >
                    Production
                  </button>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="overview-main-content">
              {/* Left Sidebar */}
              <div className="overview-left-sidebar">
                {/* Instructions */}
                <div className="instructions-section">
                  <h3>📋 Quick Start Guide</h3>
                  <div className="instruction-steps">
                    <div className="step">
                      <span className="step-number">1</span>
                      <span className="step-text">Connect your wallet to start trading</span>
                                        </div>
                    <div className="step">
                      <span className="step-number">2</span>
                      <span className="step-text">Choose your risk level (Conservative to High Risk)</span>
                                    </div>
                    <div className="step">
                      <span className="step-number">3</span>
                      <span className="step-text">Set up automated trading parameters</span>
                                            </div>
                    <div className="step">
                      <span className="step-number">4</span>
                      <span className="step-text">Monitor trades and adjust leverage as needed</span>
                                            </div>
                                            </div>
                            </div>
                            
                {/* Wallet Connection */}
                <div className="wallet-connection-section">
                  <h3>🔗 Wallet Connection</h3>
                                <div className="wallet-status">
                                    <div className="wallet-info">
                      <span className="wallet-label">Status:</span>
                      <span className={`wallet-status-indicator ${botStatus.isRunning ? 'connected' : 'disconnected'}`}>
                        {botStatus.isRunning ? 'Connected' : 'Disconnected'}
                                                        </span>
                                                    </div>
                    <div className="wallet-info">
                      <span className="wallet-label">Bybit Status:</span>
                      <span className={`wallet-status-indicator ${bybitConnected ? 'connected' : 'disconnected'}`}>
                        {bybitConnected ? '🟢 Connected' : '🔴 Not Connected'}
                      </span>
                                                    </div>
                    <div className="wallet-info">
                      <span className="wallet-label">Balance:</span>
                      <span className="wallet-balance">
                        {bybitConnected ? `$${bybitBalance.toFixed(2)}` : '$0.00'}
                      </span>
                                                    </div>
                    <div className="wallet-info">
                      <span className="wallet-label">Connection:</span>
                      <span className="wallet-network">{bybitConnectionStatus}</span>
                                                    </div>
                  </div>
                  <button className="connect-wallet-btn">
                    {botStatus.isRunning ? 'Disconnect Wallet' : 'Connect Wallet'}
                                                        </button>
                                    </div>

                {/* Backend Connection Status */}
                <div className="backend-status-section">
                  <h3>🔗 Backend Connection</h3>
                  <div className="backend-status">
                    <div className="backend-info">
                      <span className="backend-label">Status:</span>
                      <span className={`backend-status-indicator ${backendStatus.connected ? 'connected' : 'disconnected'}`}>
                        {backendStatus.connected ? 'Connected' : 'Disconnected'}
                      </span>
                    </div>
                    <div className="backend-info">
                      <span className="backend-label">Last Analysis:</span>
                      <span className="backend-analysis-time">
                        {backendStatus.lastAnalysis || 'Never'}
                      </span>
                    </div>
                    <div className="backend-info">
                      <span className="backend-label">Analysis Count:</span>
                      <span className="backend-analysis-count">{backendStatus.analysisCount}</span>
                    </div>
                  </div>
                  <div className="backend-activity">
                    <div className="activity-indicator">
                      <div className={`pulse-dot ${backendStatus.connected ? 'active' : 'inactive'}`}></div>
                      <span className="activity-text">
                        {backendStatus.connected ? 'Analyzing market data...' : 'Waiting for connection...'}
                      </span>
                    </div>
                  </div>
                </div>
                                    
                {/* Bot Controls */}
                <div className="bot-controls-section">
                  <h3>🤖 Bot Controls</h3>
                  <div className="control-buttons">
                    <button 
                      className={`control-btn ${botStatus.isRunning ? 'stop' : 'start'}`}
                      onClick={toggleBot}
                    >
                      {botStatus.isRunning ? 'Stop Bot' : 'Start Bot'}
                    </button>
                    <button 
                      className="control-btn mode-toggle"
                      onClick={() => setBotStatus(prev => ({ 
                        ...prev, 
                        simulationMode: !prev.simulationMode 
                      }))}
                    >
                      Switch to {botStatus.simulationMode ? 'Real' : 'Simulation'}
                                            </button>
                  </div>
                                        </div>
                                    </div>
                                    
              {/* Right Main Content */}
              <div className="overview-right-main">
                {/* Performance Stats */}
                <div className="performance-stats-section">
                  <h3>📊 Performance Statistics</h3>
                  <div className="stats-grid">
                       <div className="stat-card">
                         <div className="stat-icon">📈</div>
                         <div className="stat-content">
                           <div className="stat-value">{totalTrades}</div>
                           <div className="stat-label">Total Trades</div>
                         </div>
                       </div>
                       <div className="stat-card">
                         <div className="stat-icon">🎯</div>
                         <div className="stat-content">
                           <div className="stat-value">{winRate.toFixed(1)}%</div>
                           <div className="stat-label">Win Rate</div>
                         </div>
                       </div>
                       <div className="stat-card">
                         <div className="stat-icon">💰</div>
                         <div className="stat-content">
                           <div className="stat-value">{activeTrades}</div>
                           <div className="stat-label">Active Trades</div>
                         </div>
                       </div>
                       <div className="stat-card">
                         <div className="stat-icon">⚡</div>
                         <div className="stat-content">
                           <div className="stat-value">${avgProfitPerTrade.toFixed(2)}</div>
                           <div className="stat-label">Avg Profit/Trade</div>
                         </div>
                       </div>
                       <div className="stat-card">
                         <div className="stat-icon">💵</div>
                         <div className="stat-content">
                           <div className="stat-value">${totalProfit.toFixed(2)}</div>
                           <div className="stat-label">Total Profit</div>
                         </div>
                       </div>
                       <div className="stat-card">
                         <div className="stat-icon">📅</div>
                         <div className="stat-content">
                           <div className="stat-value">${dailyProfit.toFixed(2)}</div>
                           <div className="stat-label">Daily Profit</div>
                         </div>
                       </div>
                    <div className="stat-card">
                      <div className="stat-icon">📡</div>
                      <div className="stat-content">
                        <div className="stat-value">{allTrades.filter(t => t.type === 'ALERT').length}</div>
                        <div className="stat-label">Signals Generated</div>
                                                    </div>
                                            </div>
                                            </div>
                                </div>
                                
                {/* AI Trading Analysis */}
                <div className="ai-analysis-section">
                  <AITradingPanel 
                    marketData={{
                      prices: allTrades.filter(t => t.status === 'ACTIVE').map(t => t.currentPrice || t.entryPrice),
                      pair: 'BTC'
                    }}
                    trades={allTrades}
                    onExecuteTrade={addTrade}
                  />
                </div>

                {/* Recent Activity */}
                <div className="recent-activity-section">
                  <h3>🕒 Recent Activity</h3>
                  <div className="activity-list">
                    {allTrades.slice(0, 5).map(trade => (
                      <div key={trade.id} className="activity-item">
                        <div className="activity-icon">
                          {trade.type === 'MANUAL' ? '👤' : trade.type === 'AUTOMATIC' ? '🤖' : trade.type === 'AI_TRADING' ? '🤖' : '📡'}
                                        </div>
                        <div className="activity-content">
                          <div className="activity-title">
                            {trade.type} Trade - {trade.pair}
                                        </div>
                          <div className="activity-details">
                            {trade.action} ${trade.amount} at {trade.leverage}x leverage
                                        </div>
                          <div className="activity-time">
                            {new Date(trade.timestamp).toLocaleString()}
                                        </div>
                                    </div>
                        <div className={`activity-status ${trade.status}`}>
                          {trade.status}
                                </div>
                                            </div>
                    ))}
                    {allTrades.length === 0 && (
                      <div className="no-activity">
                        No recent activity. Start trading to see your activity here.
                                        </div>
                    )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

      case 'trading':
        return (
          <div className="page-content">
            {/* Bybit Connection Status Banner */}
            <div className={`bybit-status-banner ${bybitConnected ? 'connected' : 'disconnected'}`} style={{
              padding: '15px 20px',
              marginBottom: '20px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: bybitConnected ? '#d4edda' : '#f8d7da',
              border: `2px solid ${bybitConnected ? '#28a745' : '#dc3545'}`,
              color: bybitConnected ? '#155724' : '#721c24'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <span style={{ fontSize: '24px' }}>{bybitConnected ? '🟢' : '🔴'}</span>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                    Bybit Status: {bybitConnected ? 'Connected' : 'Not Connected'}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.8 }}>
                    {bybitConnectionStatus}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', opacity: 0.7 }}>Available Balance</div>
                  <div style={{ fontWeight: 'bold', fontSize: '20px' }}>
                    {bybitConnected ? `$${bybitBalance.toFixed(2)}` : '$0.00'}
                  </div>
                </div>
              </div>
            </div>
            
            <ContractTrading
              executedTrades={allTrades.filter(trade => trade.type === 'CONTRACT_TRADING')}
              addTrade={addTrade}
              updateTrade={updateTrade}
              closeTrade={closeTrade}
              prices={prices}
              isConnected={isConnected}
              connectionStatus={connectionStatus}
            />
          </div>
        );

      case 'tradfi':
        return (
          <div className="page-content">
            <TradFiTrading
              allTrades={allTrades}
              addTrade={addTrade}
              updateTrade={updateTrade}
              closeTrade={closeTrade}
              prices={prices}
              isConnected={isConnected}
              connectionStatus={connectionStatus}
            />
          </div>
        );

      case 'arbitrage':
        return (
          <div className="page-content">
            <Arbitrage />
          </div>
        );

      case 'options':
        return (
          <div className="page-content">
            <Options
              allTrades={allTrades}
              addTrade={addTrade}
              updateTrade={updateTrade}
              closeTrade={closeTrade}
              prices={prices}
              isConnected={isConnected}
              connectionStatus={connectionStatus}
            />
          </div>
        );

            case 'login':
                return (
                    <div className="page-content">
                        <Login />
                    </div>
                );

            case 'user-management':
                return (
                    <div className="page-content">
                        <UserManagement isAdminView={true} />
                    </div>
                );

            case 'credentials':
                return (
                    <div className="page-content">
                        <CredentialManager />
                    </div>
                );

            case 'trade-history':
                return (
                    <div className="page-content">
                        <TradeHistory
                            allTrades={allTrades}
                            totalProfit={totalProfit}
                            dailyProfit={dailyProfit}
                        />
                    </div>
                );

            case 'journal':
                return (
                    <div className="page-content">
                        <TradeJournal trades={allTrades} />
                    </div>
                );

            default:
                return (
                    <div className="page-content">
            <h2>Page Not Found</h2>
            <p>The page you're looking for doesn't exist.</p>
                    </div>
                );
        }
    };

    return (
        <div className="dashboard">
      {/* Header */}
            <div className="dashboard-header">
        <h1>Trading Bot Dashboard</h1>
        <div className="header-status">
          <span className={`status-dot ${botStatus.isRunning ? 'running' : 'stopped'}`}></span>
          <span>{botStatus.isRunning ? 'Running' : 'Stopped'}</span>
          <span className="mode-indicator">
            {botStatus.simulationMode ? '🎮 Simulation' : '💰 Real Trading'}
                    </span>
                </div>
            </div>

      {/* Navigation Menu */}
      <div className="dashboard-nav">
        <button 
          className={`nav-btn ${currentPage === 'overview' ? 'active' : ''}`}
          onClick={() => setCurrentPage('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`nav-btn ${currentPage === 'trading' ? 'active' : ''}`}
          onClick={() => setCurrentPage('trading')}
        >
          💰 Trading
        </button>
        <button 
          className={`nav-btn ${currentPage === 'tradfi' ? 'active' : ''}`}
          onClick={() => setCurrentPage('tradfi')}
        >
          📈 TradFi
        </button>
        <button 
          className={`nav-btn ${currentPage === 'arbitrage' ? 'active' : ''}`}
          onClick={() => setCurrentPage('arbitrage')}
        >
          🔄 Arbitrage
        </button>
        <button 
          className={`nav-btn ${currentPage === 'options' ? 'active' : ''}`}
          onClick={() => setCurrentPage('options')}
        >
          📈 Options
        </button>
        <button
          className={`nav-btn ${currentPage === 'trade-history' ? 'active' : ''}`}
          onClick={() => setCurrentPage('trade-history')}
        >
          📜 Trade History
        </button>
        <button
          className={`nav-btn ${currentPage === 'journal' ? 'active' : ''}`}
          onClick={() => setCurrentPage('journal')}
        >
          📒 Journal
        </button>
        <button 
          className={`nav-btn ${currentPage === 'login' ? 'active' : ''}`}
          onClick={() => setCurrentPage('login')}
        >
          🔐 Login
        </button>
        {environmentManager.isFeatureEnabled('userManagement') && (
          <button 
            className={`nav-btn ${currentPage === 'user-management' ? 'active' : ''}`}
            onClick={() => setCurrentPage('user-management')}
          >
            👥 Family
          </button>
        )}
        <button 
            className={`nav-btn ${currentPage === 'credentials' ? 'active' : ''}`}
            onClick={() => setCurrentPage('credentials')}
          >
            🔑 Accounts
          </button>
      </div>

            {/* Page Content */}
      <div className="dashboard-content">
            {renderPage()}
      </div>

      {/* Environment Switching Loading Popup */}
      {isSwitchingEnvironment && (
        <div className="environment-switch-popup">
          <div className="popup-content">
            <div className="loading-spinner"></div>
            <h3>🔄 Switching Environment...</h3>
            <p>Configuring system settings and reconnecting to services...</p>
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill"></div>
            </div>
          </div>
        </div>
      )}
        </div>
    );
};

const Dashboard = () => {
  return (
    <PriceProvider>
      <DashboardContent />
    </PriceProvider>
  );
};

export default Dashboard; 